import { useState, useEffect, useCallback, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "../App";
import Sidebar from "../components/Sidebar";
import { toast } from "sonner";
import { ArrowLeft, CheckCircle, ShieldCheck, Star, PaperPlaneTilt, Paperclip, X, ChatText, ChatCircleText, WhatsappLogo, Phone, Download, MagnifyingGlassPlus, Package } from "@phosphor-icons/react";
import SEO from "../components/SEO";
import LeadCaptureModal from "../components/LeadCaptureModal";
import { getCompanySettings, buildProductEnquiryWaLink } from "../utils/whatsappHelper";

const BACKEND_ORIGIN = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
const SITE_ORIGIN = typeof window !== "undefined" ? window.location.origin : BACKEND_ORIGIN;

const PublicHeader = ({ navigate }) => (
  <header className="bg-white border-b border-slate-200 shadow-sm sticky top-0 z-40">
    <div className="max-w-7xl mx-auto px-6 md:px-12 py-4 flex items-center justify-between">
      <div className="flex items-center gap-2 cursor-pointer" onClick={() => navigate('/')}>
        <ShieldCheck size={28} weight="duotone" className="text-[#002FA7]" />
        <span className="text-lg font-bold" style={{fontFamily: 'Outfit, sans-serif'}}>SwifTech India</span>
      </div>
      <button onClick={() => navigate('/auth')} className="rounded-none bg-[#002FA7] text-white hover:bg-[#00227A] px-5 py-2.5 font-medium text-sm">Login / Register</button>
    </div>
  </header>
);

const ProductDetail = ({ user, logout }) => {
  const { productId } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [brand, setBrand] = useState(null);
  const [related, setRelated] = useState([]);
  const [reviewsData, setReviewsData] = useState({ reviews: [], average: 0, total: 0, distribution: {} });
  const [loading, setLoading] = useState(true);
  const [enquiryForm, setEnquiryForm] = useState({
    name: user?.name || "", email: user?.email || "", phone: "", company: "",
    requirement: "", budget: "", timeline: ""
  });
  const [attachments, setAttachments] = useState([]); // [{name, content_type, size, data_url}]
  const [submitting, setSubmitting] = useState(false);
  const [showEnquiry, setShowEnquiry] = useState(false);
  const [showQuickQuote, setShowQuickQuote] = useState(false);
  const [quickSource, setQuickSource] = useState("product_detail");
  const [showReview, setShowReview] = useState(false);
  const [reviewForm, setReviewForm] = useState({ rating: 5, title: "", comment: "" });
  // Feb 2026 catalogue extensions
  const [activeImage, setActiveImage] = useState(0);
  const [activeTab, setActiveTab] = useState("description");
  const [qty, setQty] = useState(1);
  const [pricing, setPricing] = useState(null);
  const [companySettings, setCompanySettings] = useState(null);

  useEffect(() => { getCompanySettings().then(setCompanySettings); }, []);

  // Compute gallery images (new image_urls[] preferred, falls back to single image_url)
  const galleryImages = useMemo(() => {
    if (!product) return [];
    const imgs = (product.image_urls && product.image_urls.length > 0) ? product.image_urls : (product.image_url ? [product.image_url] : []);
    return imgs.length > 0 ? imgs : ["/assets/placeholder-product.png"];
  }, [product]);

  // Fetch tiered pricing when qty changes
  useEffect(() => {
    if (!product?.product_id) return;
    const isDealer = user?.role === "dealer";
    axios.get(`${API}/products/pricing`, { params: { product_id: product.product_id, quantity: qty, dealer: isDealer } })
      .then(r => setPricing(r.data)).catch(() => setPricing(null));
  }, [product?.product_id, qty, user?.role]);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [pRes, bRes, rRes, rvRes] = await Promise.all([
        axios.get(`${API}/products/${productId}`),
        axios.get(`${API}/brands`),
        axios.get(`${API}/products/${productId}/related`).catch(() => ({ data: [] })),
        axios.get(`${API}/products/${productId}/reviews`).catch(() => ({ data: { reviews: [], average: 0, total: 0, distribution: {} } }))
      ]);
      setProduct(pRes.data);
      const b = bRes.data.find(x => x.brand_id === pRes.data.brand_id);
      setBrand(b);
      setRelated(rRes.data);
      setReviewsData(rvRes.data);
    } catch (error) {
      console.error("Failed to fetch product:", error);
    } finally {
      setLoading(false);
    }
  }, [productId]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const submitEnquiry = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/products/${productId}/enquire`, {
        ...enquiryForm,
        attachments,
      });
      toast.success(data.message || "Enquiry sent! Our team will reach out shortly.");
      setEnquiryForm({
        name: user?.name || "", email: user?.email || "", phone: "", company: "",
        requirement: "", budget: "", timeline: ""
      });
      setAttachments([]);
      setShowEnquiry(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to send enquiry");
    } finally {
      setSubmitting(false);
    }
  };

  const submitReview = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/products/${productId}/reviews`, {
        ...reviewForm,
        product_id: productId,
        user_id: user?.user_id || "",
        user_name: user?.name || "Guest",
        verified_buyer: !!user
      });
      toast.success("Review submitted!");
      setShowReview(false);
      setReviewForm({ rating: 5, title: "", comment: "" });
      fetchAll();
    } catch (err) { toast.error("Failed to submit review"); }
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files || []);
    const MAX_FILE = 5 * 1024 * 1024; // 5 MB per file
    const MAX_TOTAL = 15 * 1024 * 1024;
    let totalSize = attachments.reduce((s, a) => s + (a.size || 0), 0);
    files.forEach(file => {
      if (file.size > MAX_FILE) { toast.error(`${file.name} exceeds 5MB`); return; }
      totalSize += file.size;
      if (totalSize > MAX_TOTAL) { toast.error("Total attachments exceed 15MB"); return; }
      const reader = new FileReader();
      reader.onload = () => {
        setAttachments(cur => [...cur, {
          name: file.name,
          content_type: file.type || "application/octet-stream",
          size: file.size,
          data_url: reader.result,
        }]);
      };
      reader.readAsDataURL(file);
    });
    e.target.value = "";
  };

  const removeAttachment = (idx) => setAttachments(cur => cur.filter((_, i) => i !== idx));

  if (loading) {
    return (
      <div className="flex min-h-screen bg-slate-50">
        {user ? <Sidebar user={user} logout={logout} /> : <PublicHeader navigate={navigate} />}
        <div className={`flex-1 ${user ? 'ml-64' : ''} flex items-center justify-center`}>
          <div className="loading-spinner"></div>
        </div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="flex min-h-screen bg-slate-50">
        {user ? <Sidebar user={user} logout={logout} /> : <PublicHeader navigate={navigate} />}
        <div className={`flex-1 ${user ? 'ml-64' : ''} flex items-center justify-center p-8`}>
          <p className="text-slate-500">Product not found.</p>
        </div>
      </div>
    );
  }

  const canonicalUrl = `${SITE_ORIGIN}/products/${product.product_id}`;
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Product",
    "name": product.name,
    "description": product.description,
    "image": product.image_url || undefined,
    "brand": brand ? { "@type": "Brand", "name": brand.name } : undefined,
    "sku": product.product_id,
    "offers": {
      "@type": "Offer",
      "priceCurrency": "INR",
      "price": product.price,
      "availability": "https://schema.org/InStock",
      "url": canonicalUrl
    },
    ...(reviewsData.total > 0 ? {
      "aggregateRating": {
        "@type": "AggregateRating",
        "ratingValue": reviewsData.average,
        "reviewCount": reviewsData.total
      },
      "review": reviewsData.reviews.slice(0, 5).map(r => ({
        "@type": "Review",
        "author": { "@type": "Person", "name": r.user_name },
        "reviewRating": { "@type": "Rating", "ratingValue": r.rating, "bestRating": 5 },
        "reviewBody": r.comment
      }))
    } : {})
  };

  return (
    <>
      <SEO
        title={`${product.name} — ${brand?.name || 'SwifTech India'} | Security System`}
        description={product.description.slice(0, 160)}
        canonical={canonicalUrl}
        image={product.image_url}
        keywords={[product.name, product.category, brand?.name, "security", "CCTV", "India", "SwifTech"].filter(Boolean).join(", ")}
        structuredData={structuredData}
      />
      <div className="flex min-h-screen bg-slate-50">
        {user ? <Sidebar user={user} logout={logout} /> : <PublicHeader navigate={navigate} />}
        <div className={`flex-1 ${user ? 'ml-64' : ''}`}>
          {/* Breadcrumb */}
          <nav className="max-w-7xl mx-auto px-6 md:px-12 py-4 text-sm text-slate-500 flex items-center gap-2">
            <button onClick={() => navigate('/')} className="hover:text-[#002FA7] transition-colors">Home</button>
            <span>/</span>
            <button onClick={() => navigate('/products')} className="hover:text-[#002FA7] transition-colors">Products</button>
            <span>/</span>
            <span className="text-slate-800 font-medium">{product.name}</span>
          </nav>

          <div className="max-w-7xl mx-auto px-6 md:px-12 py-8">
            <button data-testid="back-to-products-btn" onClick={() => navigate('/products')}
              className="mb-6 flex items-center gap-2 text-[#002FA7] hover:underline text-sm">
              <ArrowLeft size={16} /> Back to Products
            </button>

            <article className="grid grid-cols-1 lg:grid-cols-2 gap-12">
              <div>
                {/* Image gallery */}
                <div className="aspect-square overflow-hidden bg-slate-100 border border-slate-200 relative group">
                  <img src={galleryImages[activeImage]} alt={product.name} className="w-full h-full object-contain p-4 transition-transform duration-300 group-hover:scale-110"
                    onError={e => { e.target.src = "/assets/placeholder-product.png"; }} />
                  <div className="absolute top-2 right-2 bg-white/90 p-1.5 rounded-full shadow opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
                    <MagnifyingGlassPlus size={14} />
                  </div>
                </div>
                {galleryImages.length > 1 && (
                  <div className="grid grid-cols-5 gap-2 mt-3">
                    {galleryImages.map((url, i) => (
                      <button key={`${url}-${i}`} onClick={() => setActiveImage(i)} data-testid={`gallery-thumb-${i}`}
                        className={`aspect-square bg-slate-100 border-2 transition-all ${activeImage === i ? "border-[#002FA7]" : "border-slate-200 hover:border-slate-400"}`}>
                        <img src={url} alt={`${product.name} ${i + 1}`} className="w-full h-full object-contain p-1" onError={e => { e.target.src = "/assets/placeholder-product.png"; }} />
                      </button>
                    ))}
                  </div>
                )}
                {/* Video */}
                {product.video_url && (
                  <div className="mt-4 aspect-video border border-slate-200">
                    <iframe src={product.video_url.replace("watch?v=", "embed/")} title="Product video" className="w-full h-full" allowFullScreen />
                  </div>
                )}
              </div>
              <div>
                <div className="flex items-center gap-2 mb-4 flex-wrap">
                  <div data-testid="product-detail-category" className="inline-block rounded-none bg-blue-50 text-[#002FA7] px-3 py-1 text-xs uppercase tracking-wider border border-blue-200 font-bold">
                    {product.category}
                  </div>
                  {(product.tags || []).map(t => (
                    <span key={t} className="text-[10px] uppercase tracking-wider bg-amber-100 text-amber-800 px-2 py-1 font-bold">{t.replace(/_/g, ' ')}</span>
                  ))}
                  {product.stock_status === "out_of_stock" && <span className="text-[10px] uppercase tracking-wider bg-red-100 text-red-800 px-2 py-1 font-bold">Out of stock</span>}
                </div>
                {brand && <p className="text-sm text-slate-600 mb-1 uppercase tracking-wider">{brand.name}{product.model_number && <span className="text-slate-400"> · {product.model_number}</span>}</p>}
                <h1 data-testid="product-detail-name" className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-3 tracking-tight" style={{fontFamily: 'Outfit, sans-serif'}}>
                  {product.name}
                </h1>

                {/* Key spec chips */}
                {(product.resolution || product.camera_type || product.night_vision_range || product.warranty) && (
                  <div className="flex flex-wrap gap-2 mb-4 text-xs">
                    {product.resolution && <span className="bg-slate-100 px-2.5 py-1"><b>Resolution:</b> {product.resolution.toUpperCase()}</span>}
                    {product.camera_type && <span className="bg-slate-100 px-2.5 py-1 capitalize"><b>Type:</b> {product.camera_type}</span>}
                    {product.night_vision_range && <span className="bg-slate-100 px-2.5 py-1"><b>Night Vision:</b> {product.night_vision_range}</span>}
                    {product.warranty && <span className="bg-slate-100 px-2.5 py-1"><b>Warranty:</b> {product.warranty}</span>}
                    {product.indoor_outdoor && <span className="bg-slate-100 px-2.5 py-1 capitalize"><b>Placement:</b> {product.indoor_outdoor}</span>}
                  </div>
                )}

                {/* Rating summary */}
                {reviewsData.total > 0 ? (
                  <div className="flex items-center gap-2 mb-4">
                    <div className="flex">
                      {[1, 2, 3, 4, 5].map(n => (
                        <Star key={n} size={18} weight={reviewsData.average >= n ? "fill" : "regular"} className={reviewsData.average >= n ? "text-[#F59E0B]" : "text-slate-300"} />
                      ))}
                    </div>
                    <span className="text-sm font-bold text-slate-800">{reviewsData.average}</span>
                    <span className="text-sm text-slate-500">({reviewsData.total} {reviewsData.total === 1 ? 'review' : 'reviews'})</span>
                  </div>
                ) : (
                  <div className="text-sm text-slate-400 mb-4">No reviews yet · Be the first to review</div>
                )}

                {/* Tiered pricing calculator */}
                <div data-testid="pricing-calculator" className="bg-slate-50 border border-slate-200 p-4 mb-5">
                  <div className="flex items-center gap-3 mb-3">
                    <span className="text-xs uppercase tracking-wider font-bold text-slate-600">Quantity</span>
                    <div className="flex border border-slate-300 bg-white">
                      <button onClick={() => setQty(q => Math.max(1, q - 1))} className="px-3 py-1 hover:bg-slate-100" data-testid="qty-decrement">−</button>
                      <input type="number" value={qty} onChange={e => setQty(Math.max(1, Number(e.target.value) || 1))} data-testid="qty-input"
                        className="w-16 text-center outline-none border-x border-slate-200" min="1" />
                      <button onClick={() => setQty(q => q + 1)} className="px-3 py-1 hover:bg-slate-100" data-testid="qty-increment">+</button>
                    </div>
                    {pricing && (
                      <span className={`text-[11px] px-2 py-0.5 font-bold uppercase tracking-wider ${pricing.tier === "bulk" ? "bg-emerald-100 text-emerald-800" : pricing.tier === "dealer" ? "bg-blue-100 text-blue-800" : pricing.tier === "offer" ? "bg-red-100 text-red-800" : "bg-slate-200 text-slate-700"}`} data-testid="pricing-tier">{pricing.tier}</span>
                    )}
                  </div>
                  <div className="flex items-baseline gap-3">
                    <span className="text-4xl font-bold text-[#002FA7]" style={{fontFamily: 'Outfit, sans-serif'}} data-testid="product-detail-price">
                      {pricing ? `₹${pricing.unit_price.toLocaleString('en-IN')}` : `₹${product.price?.toLocaleString('en-IN')}`}
                    </span>
                    <span className="text-sm text-slate-500">/ unit</span>
                    {product.offer_price > 0 && product.retail_price > 0 && product.offer_price < product.retail_price && qty < 10 && (
                      <span className="text-sm line-through text-slate-400">₹{product.retail_price.toLocaleString('en-IN')}</span>
                    )}
                  </div>
                  {pricing && qty > 1 && (
                    <div className="text-sm text-slate-600 mt-1">Total: <b className="text-slate-900">₹{pricing.total.toLocaleString('en-IN')}</b>{pricing.saved_vs_retail > 0 && <span className="text-emerald-700 ml-2">· You save ₹{pricing.saved_vs_retail.toLocaleString('en-IN')}</span>}</div>
                  )}
                  {pricing?.next_tier && pricing.next_tier.qty_needed > 0 && (
                    <div className="text-[11px] text-amber-700 mt-2 bg-amber-50 border border-amber-200 rounded p-2">
                      💡 Add <b>{pricing.next_tier.qty_needed}</b> more unit{pricing.next_tier.qty_needed !== 1 ? "s" : ""} to unlock ₹{pricing.next_tier.price.toLocaleString('en-IN')} / unit pricing
                    </div>
                  )}
                  {product.min_order_qty > 1 && <div className="text-[11px] text-slate-500 mt-2">Minimum order: {product.min_order_qty} units</div>}
                </div>

                {/* CTAs */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-6">
                  <button data-testid="open-enquiry-btn" onClick={() => { setQuickSource("product_detail"); setShowQuickQuote(true); }}
                    className="bg-[#002FA7] text-white hover:bg-[#00227A] py-3 font-bold text-sm flex items-center justify-center gap-1.5 shadow">
                    <PaperPlaneTilt size={14} weight="bold" /> Get Quote
                  </button>
                  <button data-testid="bulk-enquiry-btn" onClick={() => { setQty(Math.max(qty, 10)); setQuickSource("product_detail_bulk"); setShowQuickQuote(true); }}
                    className="bg-amber-500 text-white hover:bg-amber-600 py-3 font-bold text-sm flex items-center justify-center gap-1.5 shadow">
                    <Package size={14} weight="bold" /> Bulk Enquiry
                  </button>
                  <a data-testid="whatsapp-btn"
                    href={product ? buildProductEnquiryWaLink(product, qty, companySettings) : "#"}
                    target="_blank" rel="noopener noreferrer"
                    className="bg-green-600 text-white hover:bg-green-700 py-3 font-bold text-sm flex items-center justify-center gap-1.5 shadow">
                    <WhatsappLogo size={14} weight="fill" /> WhatsApp
                  </a>
                  <a data-testid="call-btn" href={`tel:${companySettings?.phone || "+919999999999"}`}
                    className="bg-slate-900 text-white hover:bg-slate-700 py-3 font-bold text-sm flex items-center justify-center gap-1.5 shadow">
                    <Phone size={14} weight="fill" /> Call Now
                  </a>
                </div>

                {/* Downloads */}
                {product.datasheet_url && (
                  <a href={product.datasheet_url} target="_blank" rel="noopener noreferrer" data-testid="datasheet-link"
                    className="inline-flex items-center gap-2 text-sm text-[#002FA7] hover:underline mb-4">
                    <Download size={14} /> Download Datasheet (PDF)
                  </a>
                )}

                <div className="mt-2">
                  <button type="button" data-testid="detailed-enquiry-link" onClick={() => setShowEnquiry(true)}
                    className="inline-flex items-center gap-2 text-xs text-slate-600 hover:text-[#002FA7] hover:underline">
                    <Paperclip size={12} /> Send detailed enquiry with attachments (drawings, BOM, site photos)
                  </button>
                </div>
              </div>
            </article>

            {/* ===== Spec Tabs ===== */}
            <section className="mt-10 border-t border-slate-200 pt-6">
              <div className="flex gap-1 border-b border-slate-200 mb-5 flex-wrap">
                {[
                  { k: "description", label: "Description" },
                  { k: "features", label: "Features" },
                  { k: "specs", label: "Specifications" },
                  { k: "package", label: "Package Contents" },
                  { k: "reviews", label: `Reviews ${reviewsData.total ? `(${reviewsData.total})` : ""}` },
                ].map(t => (
                  <button key={t.k} data-testid={`tab-${t.k}`} onClick={() => setActiveTab(t.k)}
                    className={`px-5 py-2.5 text-sm font-medium transition-all border-b-2 -mb-px ${activeTab === t.k ? "border-[#002FA7] text-[#002FA7]" : "border-transparent text-slate-500 hover:text-slate-800"}`}>
                    {t.label}
                  </button>
                ))}
              </div>
              <div className="max-w-4xl">
                {activeTab === "description" && (
                  <p data-testid="product-detail-description" className="text-base text-slate-700 leading-relaxed whitespace-pre-line">
                    {product.description || "No description provided."}
                  </p>
                )}
                {activeTab === "features" && (
                  (product.key_features?.length > 0 || product.features?.length > 0) ? (
                    <ul className="space-y-2 text-sm">
                      {(product.key_features?.length ? product.key_features : product.features).map((f, i) => (
                        <li key={`feat-${i}-${f.slice(0, 20)}`} className="flex gap-2"><CheckCircle size={18} weight="fill" className="text-emerald-500 shrink-0 mt-0.5" /><span>{f}</span></li>
                      ))}
                    </ul>
                  ) : <p className="text-sm text-slate-500">No features listed.</p>
                )}
                {activeTab === "specs" && (
                  product.technical_specs?.length > 0 ? (
                    <table className="w-full text-sm">
                      <tbody>
                        {product.technical_specs.map((s, i) => (
                          <tr key={`spec-${i}`} className="border-b border-slate-100">
                            <td className="py-2 text-slate-500 w-1/3 font-medium">{s.label}</td>
                            <td className="py-2 text-slate-800">{s.value}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  ) : Object.keys(product.specifications || {}).length > 0 ? (
                    <table className="w-full text-sm">
                      <tbody>
                        {Object.entries(product.specifications).map(([k, v]) => (
                          <tr key={k} className="border-b border-slate-100">
                            <td className="py-2 text-slate-500 w-1/3 font-medium capitalize">{k.replace(/_/g, ' ')}</td>
                            <td className="py-2 text-slate-800">{String(v)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  ) : <p className="text-sm text-slate-500">No specifications added yet.</p>
                )}
                {activeTab === "package" && (
                  product.package_contents?.length > 0 ? (
                    <ul className="space-y-2 text-sm">
                      {product.package_contents.map((item, i) => (
                        <li key={`pkg-${i}`} className="flex gap-2"><Package size={16} className="text-slate-500 shrink-0 mt-0.5" /><span>{item}</span></li>
                      ))}
                    </ul>
                  ) : <p className="text-sm text-slate-500">Package contents not listed.</p>
                )}
                {activeTab === "reviews" && (
                  <div data-testid="reviews-panel">
                    {/* Reuse existing reviews block via button */}
                    <button onClick={() => setShowReview(true)} className="mb-4 border border-[#002FA7] text-[#002FA7] hover:bg-blue-50 px-4 py-2 text-sm font-bold">Write a review</button>
                    {reviewsData.reviews.length === 0 ? <p className="text-sm text-slate-500">No reviews yet.</p> : (
                      <div className="space-y-4">
                        {reviewsData.reviews.map(r => (
                          <div key={r.review_id} className="border-b border-slate-100 pb-3">
                            <div className="flex items-center gap-2 mb-1">
                              <div className="flex">{[1, 2, 3, 4, 5].map(n => <Star key={n} size={14} weight={r.rating >= n ? "fill" : "regular"} className={r.rating >= n ? "text-[#F59E0B]" : "text-slate-300"} />)}</div>
                              <span className="text-sm font-bold">{r.title || r.user_name}</span>
                              <span className="text-xs text-slate-500">· {r.user_name}</span>
                            </div>
                            <p className="text-sm text-slate-700">{r.comment}</p>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </section>
            {/* ===== END Spec Tabs ===== */}

            {/* ===== Hidden original spec block — now shown via tabs above ===== */}
            <div style={{ display: "none" }}>

                {product.features?.length > 0 && (
                  <div className="mb-8">
                    <h3 className="text-lg font-semibold mb-3" style={{fontFamily: 'Outfit, sans-serif'}}>Key Features</h3>
                    <ul className="space-y-2">
                      {product.features.map((f, i) => (
                        <li key={`legacy-feat-${i}`} data-testid={`feature-${i}`} className="flex items-center gap-3 text-sm text-slate-700">
                          <CheckCircle size={18} weight="fill" className="text-[#10B981]" />
                          {f}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {product.specifications && Object.keys(product.specifications).length > 0 && (
                  <div className="mb-8">
                    <h3 className="text-lg font-semibold mb-3" style={{fontFamily: 'Outfit, sans-serif'}}>Specifications</h3>
                    <dl className="grid grid-cols-2 gap-3 text-sm">
                      {Object.entries(product.specifications).map(([k, v]) => (
                        <div key={k} data-testid={`spec-${k}`} className="border-b border-slate-200 py-2">
                          <dt className="text-xs uppercase tracking-wider text-slate-500">{k}</dt>
                          <dd className="font-medium text-slate-800">{String(v)}</dd>
                        </div>
                      ))}
                    </dl>
                  </div>
                )}
              </div>
            {/* end hidden */}

            {/* REVIEWS SECTION */}
            <section className="mt-16 pt-8 border-t border-slate-200" id="reviews">
              <div className="flex justify-between items-end mb-6 flex-wrap gap-3">
                <div>
                  <h2 className="text-3xl font-bold" style={{fontFamily: 'Outfit, sans-serif'}}>Customer Reviews</h2>
                  <p className="text-sm text-slate-500">{reviewsData.total} total {reviewsData.total === 1 ? 'review' : 'reviews'}</p>
                </div>
                <button data-testid="write-review-btn" onClick={() => setShowReview(!showReview)}
                  className="rounded-none bg-white border border-[#002FA7] text-[#002FA7] hover:bg-[#002FA7] hover:text-white px-5 py-2.5 text-sm font-medium flex items-center gap-2 transition-colors duration-300">
                  <ChatCircleText size={16} weight="bold" /> {showReview ? "Cancel" : "Write a Review"}
                </button>
              </div>

              {showReview && (
                <form onSubmit={submitReview} data-testid="review-form" className="bg-white border border-slate-200 p-6 mb-6 space-y-4 shadow-sm">
                  <div>
                    <label className="text-xs uppercase tracking-[0.2em] font-bold text-slate-500 block mb-2">Your Rating</label>
                    <div className="flex gap-1">
                      {[1, 2, 3, 4, 5].map(n => (
                        <button key={n} type="button" data-testid={`review-rating-${n}`}
                          onClick={() => setReviewForm({ ...reviewForm, rating: n })}
                          className={`w-10 h-10 flex items-center justify-center border transition-colors duration-300 ${reviewForm.rating >= n ? 'bg-[#F59E0B] border-[#F59E0B] text-white' : 'bg-white border-slate-300 text-slate-400'}`}>
                          <Star size={18} weight={reviewForm.rating >= n ? "fill" : "regular"} />
                        </button>
                      ))}
                    </div>
                  </div>
                  <input data-testid="review-title" placeholder="Review title (optional)" value={reviewForm.title}
                    onChange={e => setReviewForm({ ...reviewForm, title: e.target.value })}
                    className="w-full rounded-none border border-slate-300 focus:border-[#002FA7] p-3 outline-none" />
                  <textarea data-testid="review-comment" required rows={4} placeholder="What did you like (or not)?" value={reviewForm.comment}
                    onChange={e => setReviewForm({ ...reviewForm, comment: e.target.value })}
                    className="w-full rounded-none border border-slate-300 focus:border-[#002FA7] p-3 outline-none" />
                  <button type="submit" data-testid="submit-review-btn"
                    className="rounded-none bg-[#002FA7] text-white hover:bg-[#00227A] px-6 py-3 font-medium transition-colors duration-300 flex items-center gap-2">
                    <PaperPlaneTilt size={16} weight="bold" /> Submit Review
                  </button>
                </form>
              )}

              {reviewsData.total === 0 ? (
                <p className="text-sm text-slate-500 text-center py-10 bg-white border border-slate-200" data-testid="no-reviews">
                  No reviews yet. Be the first to share your experience.
                </p>
              ) : (
                <div className="space-y-4">
                  {reviewsData.reviews.map(r => (
                    <div key={r.review_id} data-testid={`review-${r.review_id}`} className="bg-white border border-slate-200 p-5">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-3">
                          <div className="flex">
                            {[1, 2, 3, 4, 5].map(n => (
                              <Star key={n} size={14} weight={r.rating >= n ? "fill" : "regular"} className={r.rating >= n ? "text-[#F59E0B]" : "text-slate-300"} />
                            ))}
                          </div>
                          <span className="font-semibold text-sm">{r.user_name}</span>
                          {r.verified_buyer && (
                            <span className="text-[0.6rem] uppercase tracking-wider font-bold bg-green-50 text-green-700 border border-green-200 px-2 py-0.5">Verified</span>
                          )}
                        </div>
                        <span className="text-xs text-slate-400">{new Date(r.created_at).toLocaleDateString()}</span>
                      </div>
                      {r.title && <h4 className="font-semibold mb-1">{r.title}</h4>}
                      <p className="text-sm text-slate-700">{r.comment}</p>
                    </div>
                  ))}
                </div>
              )}
            </section>

            {/* RELATED PRODUCTS */}
            {related.length > 0 && (
              <section className="mt-16 pt-8 border-t border-slate-200">
                <h2 className="text-3xl font-bold mb-6" style={{fontFamily: 'Outfit, sans-serif'}}>Related Products</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  {related.map(p => (
                    <button key={p.product_id} data-testid={`related-${p.product_id}`}
                      onClick={() => navigate(`/products/${p.product_id}`)}
                      className="bg-white border border-slate-200 hover:shadow-md transition-shadow duration-300 text-left group">
                      {p.image_url && (
                        <div className="aspect-square overflow-hidden bg-slate-100">
                          <img src={p.image_url} alt={p.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                        </div>
                      )}
                      <div className="p-4">
                        <div className="text-xs uppercase tracking-wider text-slate-500 mb-1">{p.category}</div>
                        <div className="font-semibold text-sm mb-1 line-clamp-2">{p.name}</div>
                        <div className="text-lg font-bold text-[#002FA7]" style={{fontFamily: 'Outfit, sans-serif'}}>₹{p.price?.toLocaleString('en-IN')}</div>
                      </div>
                    </button>
                  ))}
                </div>
              </section>
            )}
          </div>
        </div>
      </div>

      {/* ENQUIRY MODAL */}
      {showEnquiry && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          onClick={() => setShowEnquiry(false)} data-testid="enquiry-modal">
          <div className="bg-white shadow-2xl w-full max-w-2xl max-h-[92vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="sticky top-0 bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between z-10">
              <div>
                <h3 className="text-xl font-bold" style={{ fontFamily: "Outfit, sans-serif" }}>Request Enquiry / Quote</h3>
                <p className="text-xs text-slate-500 mt-0.5">For: <b>{product.name}</b> · Reply within 1 business day</p>
              </div>
              <button onClick={() => setShowEnquiry(false)} className="p-2 hover:bg-slate-100" data-testid="close-enquiry-btn"><X size={18} /></button>
            </div>
            <form onSubmit={submitEnquiry} data-testid="enquiry-form" className="p-6 space-y-5">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-xs uppercase tracking-[0.2em] font-bold text-slate-500 block mb-2">Full Name *</label>
                  <input data-testid="enquiry-name" required value={enquiryForm.name}
                    onChange={e => setEnquiryForm({ ...enquiryForm, name: e.target.value })}
                    className="w-full rounded-none border border-slate-300 focus:border-[#002FA7] p-3 outline-none" />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-[0.2em] font-bold text-slate-500 block mb-2">Email *</label>
                  <input data-testid="enquiry-email" type="email" required value={enquiryForm.email}
                    onChange={e => setEnquiryForm({ ...enquiryForm, email: e.target.value })}
                    className="w-full rounded-none border border-slate-300 focus:border-[#002FA7] p-3 outline-none" />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-[0.2em] font-bold text-slate-500 block mb-2">Phone / WhatsApp</label>
                  <input data-testid="enquiry-phone" value={enquiryForm.phone}
                    onChange={e => setEnquiryForm({ ...enquiryForm, phone: e.target.value })}
                    placeholder="+91 …"
                    className="w-full rounded-none border border-slate-300 focus:border-[#002FA7] p-3 outline-none" />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-[0.2em] font-bold text-slate-500 block mb-2">Company / Organization</label>
                  <input data-testid="enquiry-company" value={enquiryForm.company}
                    onChange={e => setEnquiryForm({ ...enquiryForm, company: e.target.value })}
                    className="w-full rounded-none border border-slate-300 focus:border-[#002FA7] p-3 outline-none" />
                </div>
              </div>

              <div>
                <label className="text-xs uppercase tracking-[0.2em] font-bold text-slate-500 block mb-2">
                  <ChatText size={14} className="inline mr-1" /> Requirement Brief *
                </label>
                <textarea data-testid="enquiry-requirement" required rows={4}
                  value={enquiryForm.requirement}
                  onChange={e => setEnquiryForm({ ...enquiryForm, requirement: e.target.value })}
                  placeholder="Describe the site, number of cameras, coverage area, special needs (night vision, 24×7 recording, app access)…"
                  className="w-full rounded-none border border-slate-300 focus:border-[#002FA7] p-3 outline-none" />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-xs uppercase tracking-[0.2em] font-bold text-slate-500 block mb-2">Approx. Budget (optional)</label>
                  <input data-testid="enquiry-budget" value={enquiryForm.budget}
                    onChange={e => setEnquiryForm({ ...enquiryForm, budget: e.target.value })}
                    placeholder="e.g., ₹25,000 – ₹50,000"
                    className="w-full rounded-none border border-slate-300 focus:border-[#002FA7] p-3 outline-none" />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-[0.2em] font-bold text-slate-500 block mb-2">Timeline (optional)</label>
                  <input data-testid="enquiry-timeline" value={enquiryForm.timeline}
                    onChange={e => setEnquiryForm({ ...enquiryForm, timeline: e.target.value })}
                    placeholder="e.g., Within 2 weeks"
                    className="w-full rounded-none border border-slate-300 focus:border-[#002FA7] p-3 outline-none" />
                </div>
              </div>

              <div>
                <label className="text-xs uppercase tracking-[0.2em] font-bold text-slate-500 block mb-2">
                  <Paperclip size={14} className="inline mr-1" /> Attach documents (5 MB each · 15 MB total)
                </label>
                <label className="inline-flex items-center gap-2 rounded-none border border-dashed border-slate-400 px-4 py-3 text-sm text-slate-600 hover:bg-slate-50 cursor-pointer">
                  <Paperclip size={16} />
                  <span>Click to upload</span>
                  <input data-testid="enquiry-files" type="file" multiple
                    onChange={handleFileSelect}
                    accept="image/*,application/pdf,.doc,.docx,.xls,.xlsx"
                    className="sr-only" />
                </label>
                {attachments.length > 0 && (
                  <ul className="mt-3 space-y-2" data-testid="enquiry-attachments">
                    {attachments.map((a, i) => (
                      <li key={`${a.name}-${i}`} className="flex items-center justify-between border border-slate-200 bg-slate-50 px-3 py-2 text-sm">
                        <div className="flex items-center gap-2 min-w-0">
                          <Paperclip size={14} className="text-slate-500 shrink-0" />
                          <span className="truncate font-medium">{a.name}</span>
                          <span className="text-xs text-slate-500 shrink-0">{(a.size / 1024).toFixed(1)} KB</span>
                        </div>
                        <button type="button" onClick={() => removeAttachment(i)}
                          data-testid={`remove-attachment-${i}`}
                          className="p-1 hover:bg-red-50 text-red-500" aria-label="Remove"><X size={14} /></button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              <div className="flex items-center justify-end gap-3 pt-4 border-t border-slate-200">
                <button type="button" onClick={() => setShowEnquiry(false)}
                  className="rounded-none border border-slate-300 px-5 py-2.5 hover:bg-slate-50">Cancel</button>
                <button type="submit" disabled={submitting} data-testid="submit-enquiry-btn"
                  className="rounded-none bg-[#002FA7] text-white hover:bg-[#00227A] px-6 py-3 font-bold transition flex items-center gap-2 disabled:opacity-60">
                  <PaperPlaneTilt size={16} weight="bold" /> {submitting ? "Sending…" : "Send Enquiry"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <LeadCaptureModal
        open={showQuickQuote}
        onClose={() => setShowQuickQuote(false)}
        product={product}
        user={user}
        defaultQuantity={qty}
        source={quickSource}
      />
    </>
  );
};

export default ProductDetail;
